import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OidcAuthService } from '../oidc-auth.service';
import { LoginService } from '../../login/login.service';
import { FcmRegistrationService } from '../../notifications/fcm-registration.service';
import { SelectedStudentService } from '../../shared/selected-student.service';

/**
 * Lands the Authorization Code redirect from QLM: exchanges the code (via PKCE) for
 * tokens, then routes onward.
 *
 * Magic-link hand-off:
 * - Email tab: `__qlm_magic__` state → thank-you after exchange.
 * - Waiting tab: claim → `/login?magic_wake=1` (same-device adopts email tokens;
 *   cross-device quiet-authorizes against the claim AS session). Claim must not
 *   `finishLogin` the pre-email interaction — that resets the shared session uid and
 *   invalidates the email tab’s authorization code on same-device.
 */
const RETRY_STAMP_KEY = 'oidc-callback-retry-at';
const RETRY_WINDOW_MS = 60 * 1000;

@Component({
  selector: 'oidc-callback',
  templateUrl: './oidc-callback.component.html',
  styleUrls: ['./oidc-callback.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OidcCallbackComponent implements OnInit {
  /** Email-link tab: show “close this tab / continue here” instead of entering the app. */
  magicLinkComplete = false;

  /** Captured before tryLogin strips `state` from the URL. */
  private isMagicEmailTab = false;

  constructor(
    private readonly oidc: OidcAuthService,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly loginService: LoginService,
    private readonly fcmRegistration: FcmRegistrationService,
    private readonly selectedStudentService: SelectedStudentService,
  ) {}

  async ngOnInit(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    this.isMagicEmailTab = this.oidc.stateIndicatesMagicCompletion(
      params.get('state'),
    );

    if (params.get('error')) {
      this.router.navigate(['/login'], { queryParams: { silent: 'failed' } });
      return;
    }

    try {
      const ok = await this.oidc.completeLogin();
      if (!ok) {
        await this.recoverOrRetry(/* showSnack */ false);
        return;
      }
      await this.afterLoginEstablished();
    } catch {
      await this.recoverOrRetry(/* showSnack */ true);
    }
  }

  /** Stay on this device instead of returning to the waiting tab. */
  continueHere(): void {
    this.magicLinkComplete = false;
    this.navigateToDestination();
  }

  private async afterLoginEstablished(): Promise<void> {
    try {
      await this.loginService.getUserInfo();
    } catch {
      // Email tab: thank-you does not need user-info. "Continue here" can re-auth.
      if (this.isMagicEmailTab) {
        this.clearLoopBreakers();
        this.magicLinkComplete = true;
        return;
      }
      throw new Error('user-info failed');
    }
    this.clearLoopBreakers();
    try {
      await this.fcmRegistration.registerBrowserToken();
    } catch {
      // Fail silently; do not block redirect.
    }
    if (this.isMagicEmailTab) {
      this.magicLinkComplete = true;
      return;
    }
    this.navigateToDestination();
  }

  /**
   * Email-tab safety net: exchange flaked but a live opaque token is already present
   * (library stores access_token before id_token nonce validation). Prefer thank-you
   * over the login button.
   */
  private async finishAsMagicThankYou(): Promise<boolean> {
    if (!this.isMagicEmailTab) {
      return false;
    }
    if (this.oidc.isLoggedIn) {
      try {
        await this.afterLoginEstablished();
        return true;
      } catch {
        // fall through to thank-you without tokens
      }
    }
    // Same-device: waiting tab enters the app; this tab still shows thank-you.
    this.clearLoopBreakers();
    this.magicLinkComplete = true;
    return true;
  }

  private async recoverOrRetry(showSnack: boolean): Promise<void> {
    if (await this.finishAsMagicThankYou()) {
      return;
    }
    if (this.retryOnce()) {
      return;
    }
    if (showSnack) {
      this.snackBar.open('Login failed. Please try again.', 'dismiss', {
        duration: 3000,
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    }
    this.router.navigate(['/login'], { queryParams: { silent: 'failed' } });
  }

  private clearLoopBreakers(): void {
    sessionStorage.removeItem('oidc-auto-login-at');
    sessionStorage.removeItem(RETRY_STAMP_KEY);
    this.oidc.clearFlowStamp();
  }

  private navigateToDestination(): void {
    const raw = this.oidc.postLoginTarget;
    const redirectUrl = raw && raw.startsWith('/') ? raw : undefined;
    const dest = this.loginService.resolvePostLoginTarget(redirectUrl);
    if ('navigateByUrl' in dest) {
      this.router.navigateByUrl(dest.navigateByUrl);
    } else {
      if (dest.clearSelectedStudent) {
        this.selectedStudentService.clear();
      }
      this.router.navigate(dest.navigate);
    }
  }

  private retryOnce(): boolean {
    const stampedAt = Number(sessionStorage.getItem(RETRY_STAMP_KEY));
    if (stampedAt && Date.now() - stampedAt < RETRY_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(RETRY_STAMP_KEY, String(Date.now()));
    this.oidc.startLogin(this.oidc.postLoginTarget, {
      magicCompletion: this.isMagicEmailTab,
    });
    return true;
  }
}
